import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { data, error } = await supabase
          .from('blogs')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        setBlogs(data || []);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const featuredArticle = blogs.length > 0 ? blogs[0] : null;
  const gridArticles = blogs.length > 1 ? blogs.slice(1) : [];
  const latestPosts = blogs.slice(0, 4);

  // Dummy categories
  const categories = [
    { name: 'Market Insights', count: '18', icon: 'analytics' },
    { name: 'Property Buying Tips', count: '14', icon: 'real_estate_agent' },
    { name: 'Investment Guide', count: '12', icon: 'trending_up' },
    { name: 'Project Updates', count: '20', icon: 'domain' },
    { name: 'Lifestyle & Living', count: '10', icon: 'weekend' },
    { name: 'Company News', count: '08', icon: 'newspaper' },
  ];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Real Estate Insights & News | HBB0011 REALTY"
        description="Stay updated with the latest news, market insights, and architectural trends from the experts at HBB0011 REALTY."
        keywords="real estate blog, market insights, architectural trends, property news, HBB0011 REALTY blog"
      />
      
      {/* 1. Hero Section (No Founder Photo) */}
      <header className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 lg:min-h-[50vh] flex items-center overflow-hidden border-b border-gray-800">
        
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0">
          <img 
            src={buildingImg} 
            alt="Luxury real estate background" 
            className="w-full h-full object-cover opacity-20 mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-[#041424]/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-center lg:text-left mx-auto lg:mx-0">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
              <span className="text-[#D4AF37] uppercase">Blog</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight uppercase">
              Blog & News
            </h1>
            
            <h2 className="text-lg sm:text-xl md:text-2xl text-[#D4AF37] font-bold mb-6 uppercase tracking-widest leading-relaxed">
              Stay Informed. Stay Ahead.
            </h2>
            
            <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Insights, trends, and expert perspectives on real estate, lifestyle, and investment.
            </p>
          </div>
        </div>
      </header>

      {/* 2. Main Editorial Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* Left Column (Main Articles) */}
          <div className="lg:col-span-8 flex flex-col gap-12">
            
            {/* Featured Article */}
            {loading ? (
              <div className="bg-[#06121e] border border-gray-800 rounded-sm h-[400px] flex items-center justify-center">
                <span className="material-symbols-outlined animate-spin text-[#D4AF37] text-4xl">sync</span>
              </div>
            ) : featuredArticle ? (
              <article className="bg-[#06121e] border border-gray-800 rounded-sm overflow-hidden flex flex-col md:flex-row group hover:border-[#D4AF37]/50 transition-colors duration-500 shadow-xl relative">
                <div className="absolute top-4 left-4 bg-[#D4AF37] text-[#041424] text-[10px] sm:text-xs font-bold px-3 py-1 uppercase tracking-widest z-20">
                  Featured Article
                </div>
                
                <div className="w-full md:w-1/2 h-64 md:h-auto relative overflow-hidden shrink-0 bg-[#041424]">
                  <img src={featuredArticle.image_url || buildingImg} alt="Featured Article" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#041424]/20 group-hover:bg-transparent transition-colors duration-500"></div>
                </div>
                
                <div className="p-6 sm:p-8 md:w-1/2 flex flex-col justify-center">
                  <div className="flex items-center gap-2 text-gray-400 text-[10px] sm:text-xs mb-3 font-medium tracking-wider uppercase">
                    <span className="material-symbols-outlined text-sm">calendar_month</span>
                    {new Date(featuredArticle.created_at).toLocaleDateString()}
                  </div>
                  <h3 className="text-xl sm:text-2xl text-white font-bold mb-4 leading-tight group-hover:text-[#D4AF37] transition-colors">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-8 line-clamp-3">
                    {featuredArticle.content}
                  </p>
                  <Link to="#" className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#041424] px-6 py-3 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors w-fit">
                    Read More <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </Link>
                </div>
              </article>
            ) : (
              <div className="bg-[#06121e] border border-gray-800 rounded-sm p-8 text-center text-gray-400">
                <p>No blog posts available yet.</p>
              </div>
            )}

            {/* All Articles Grid */}
            {!loading && gridArticles.length > 0 && (
              <div>
                <div className="border-b border-gray-800 pb-4 mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wider">All Articles</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                  {gridArticles.map((article) => (
                    <article key={article.id} className="bg-[#06121e] border border-gray-800 rounded-sm overflow-hidden group hover:border-[#D4AF37]/50 transition-colors duration-500 flex flex-col shadow-lg">
                      <div className="relative h-48 sm:h-56 overflow-hidden shrink-0 bg-[#041424]">
                        <img src={article.image_url || buildingImg} alt={article.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-[#041424]/20 group-hover:bg-transparent transition-colors duration-500"></div>
                      </div>
                      <div className="p-5 sm:p-6 flex flex-col flex-grow">
                        <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] sm:text-xs mb-3 font-medium tracking-wider uppercase">
                          <span className="material-symbols-outlined text-sm">calendar_month</span>
                          {new Date(article.created_at).toLocaleDateString()}
                        </div>
                        <h4 className="text-base sm:text-lg font-bold text-white mb-3 leading-tight group-hover:text-[#D4AF37] transition-colors">
                          {article.title}
                        </h4>
                        <p className="text-gray-400 text-xs leading-relaxed mb-6 flex-grow line-clamp-3">
                          {article.content}
                        </p>
                        <Link to="#" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:text-white transition-colors mt-auto">
                          Read More <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column (Sidebar) */}
          <aside className="lg:col-span-4 flex flex-col gap-8">
            
            {/* Categories */}
            <div className="bg-[#06121e] border border-gray-800 rounded-sm p-6 sm:p-8">
              <h4 className="text-white text-sm font-bold uppercase tracking-widest border-b border-gray-800 pb-4 mb-6">Categories</h4>
              <ul className="space-y-4">
                {categories.map((cat, index) => (
                  <li key={index}>
                    <Link to="#" className="flex justify-between items-center group">
                      <div className="flex items-center gap-3 text-gray-400 group-hover:text-[#D4AF37] transition-colors">
                        <span className="material-symbols-outlined text-lg">{cat.icon}</span>
                        <span className="text-xs sm:text-sm font-medium tracking-wide">{cat.name}</span>
                      </div>
                      <span className="text-gray-500 text-[10px] font-mono border border-gray-700 px-2 py-0.5 rounded-sm group-hover:border-[#D4AF37] group-hover:text-[#D4AF37] transition-colors">
                        {cat.count}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Latest Posts */}
            <div className="bg-[#06121e] border border-gray-800 rounded-sm p-6 sm:p-8">
              <h4 className="text-white text-sm font-bold uppercase tracking-widest border-b border-gray-800 pb-4 mb-6">Latest Posts</h4>
              <div className="flex flex-col gap-6">
                {loading ? (
                  <div className="text-gray-400 text-sm">Loading...</div>
                ) : latestPosts.length > 0 ? latestPosts.map((post) => (
                  <Link key={post.id} to="#" className="flex items-center gap-4 group">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-sm overflow-hidden shrink-0 border border-gray-700 group-hover:border-[#D4AF37] transition-colors bg-[#041424]">
                      <img src={post.image_url || buildingImg} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <h6 className="text-white text-xs sm:text-sm font-medium leading-snug group-hover:text-[#D4AF37] transition-colors line-clamp-2 mb-2">
                        {post.title}
                      </h6>
                      <div className="flex items-center gap-1 text-gray-500 text-[9px] sm:text-[10px] uppercase tracking-wider">
                        <span className="material-symbols-outlined text-[12px]">calendar_month</span>
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                )) : (
                  <div className="text-gray-500 text-sm">No recent posts.</div>
                )}
              </div>
            </div>

            {/* Newsletter Subscribe */}
            <div className="bg-[#06121e] border border-[#D4AF37]/30 rounded-sm p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent pointer-events-none"></div>
              <div className="relative z-10">
                <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-3">Subscribe to our Newsletter</h4>
                <p className="text-gray-400 text-xs leading-relaxed mb-6">Stay updated with the latest news, projects, and exclusive offers.</p>
                <form className="flex flex-col gap-4">
                  <input 
                    type="email" 
                    placeholder="Enter your email address" 
                    className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-3 text-white text-xs focus:border-[#D4AF37] focus:outline-none transition-colors"
                  />
                  <button type="submit" className="w-full bg-[#D4AF37] text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest py-3 rounded-sm hover:bg-white transition-colors flex items-center justify-center gap-2">
                    Subscribe <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </button>
                </form>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-4 text-center">We respect your privacy. Unsubscribe anytime.</p>
              </div>
            </div>

          </aside>

        </div>
      </main>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/919916710011" target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50">
        <span className="material-symbols-outlined text-white text-3xl">chat</span>
      </a>

    </div>
  );
};

export default Blog;